import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { useUser } from "@/components/UserProvider";
import {
  ActionConfirmationModal,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  InlineEditableField,
  Input,
  MultiSelect,
  Separator,
  Skeleton,
  Typography,
} from "@/components/ui";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  useCreateMatterTaskPlan,
  useDeleteMatterTaskPlan,
  useUpdateMatterTaskPlan,
} from "@/lib/data/react-query/matters/matter-task-plan";
import { MatterTaskPlanSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { MatterTaskPlan, IncidentType } from "@/lib/types";
import { TaskPlanTasksSection } from "@/components/onboarding/steps/TaskPlanTasksSection";
import { ErrorBoundary } from "@sentry/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { isEqual, sortBy, toLower } from "lodash";
import { Check, Trash2 } from "lucide-react";
import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
} from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = z.object({
  title: MatterTaskPlanSchema.shape.title,
  description: MatterTaskPlanSchema.shape.description,
  incidentTypeIds: z.array(z.string()).min(1, {
    message: "Incident type is required",
  }),
});

const defaultFormValues: z.infer<typeof formSchema> = {
  title: "",
  description: "",
  incidentTypeIds: [],
};

const inlineFieldSchemas = {
  title: MatterTaskPlanSchema.shape.title,
  description: MatterTaskPlanSchema.shape.description,
  incidentType: formSchema.shape.incidentTypeIds,
};

type AddTaskPlanEditorProps = {
  taskPlans: MatterTaskPlan[];
  incidentTypes: IncidentType[];
  isReadOnly: boolean;
  onClose: () => void;
};

function AddTaskPlanEditor({
  taskPlans,
  incidentTypes,
  isReadOnly,
  onClose,
}: AddTaskPlanEditorProps) {
  const [selectedIncidentTypes, setSelectedIncidentTypes] = useState<string[]>(
    defaultFormValues.incidentTypeIds,
  );
  const createMatterTaskPlan = useCreateMatterTaskPlan({ showToast: false });
  const isFormLoading = createMatterTaskPlan.isPending;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  useEffect(() => {
    form.reset(defaultFormValues);
    setSelectedIncidentTypes(defaultFormValues.incidentTypeIds);
  }, [form]);

  const createErrorMessage = useMemo(() => {
    const errors = [
      form.formState.errors.title?.message,
      form.formState.errors.description?.message,
      form.formState.errors.incidentTypeIds?.message,
    ].filter(Boolean);

    return errors.length > 0 ? String(errors[0]) : null;
  }, [form.formState.errors]);

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (
      taskPlans.some(
        (plan) => toLower(plan.title.trim()) === toLower(data.title.trim()),
      )
    ) {
      form.setError("title", {
        message: "Task plan title must be unique",
      });
      return;
    }

    createMatterTaskPlan.mutate({
      title: data.title,
      description: data.description,
      incidentTypeIds: data.incidentTypeIds,
    });
    onClose();
    form.reset(defaultFormValues);
    setSelectedIncidentTypes(defaultFormValues.incidentTypeIds);
  };

  return (
    <div>
      <Form {...form}>
        <form
          className="relative w-full"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <fieldset
            disabled={isReadOnly || isFormLoading}
            className="bg-muted/20 flex w-full flex-col gap-2 rounded-xl px-2 py-1.5 lg:flex-row lg:items-center lg:justify-between"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="New task plan title"
                          className="border-border/40 h-9 w-56 bg-white px-2 py-0.5 text-sm leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="min-w-0 space-y-0">
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          placeholder="Add description"
                          className="border-border/40 h-9 w-60 bg-white px-2 py-0.5 text-xs leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="incidentTypeIds"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <MultiSelect
                          selectedValues={selectedIncidentTypes}
                          setSelectedValues={setSelectedIncidentTypes}
                          options={incidentTypes.map((incidentType) => ({
                            label: getIncidentTypeLabel(incidentType),
                            value: incidentType.id,
                          }))}
                          placeholder={
                            incidentTypes.length === 0
                              ? "No incident types"
                              : "Incident types"
                          }
                          disabled={incidentTypes.length === 0}
                          onValueChange={(values) => {
                            setSelectedIncidentTypes(values);
                            field.onChange(values);
                          }}
                          maxCount={1}
                          modalPopover
                          showClearButton={false}
                          className="border-border/40 h-9 w-56 bg-white px-1 py-0.5 text-xs leading-normal shadow-none"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              {createErrorMessage ? (
                <Typography className="text-destructive text-xs">
                  {createErrorMessage}
                </Typography>
              ) : null}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="submit"
                variant="ghost"
                size="xs"
                disabled={isReadOnly || isFormLoading}
                aria-label="Save new task plan"
              >
                <Check />
              </Button>
            </div>
          </fieldset>

          <button type="submit" className="hidden" aria-hidden="true" />
        </form>
      </Form>
    </div>
  );
}
type IncidentTypeSelectorProps = {
  taskPlan: MatterTaskPlan;
  incidentTypes: IncidentType[];
  canUpdate: boolean;
  onChange: (taskPlan: MatterTaskPlan, nextValues: string[]) => boolean;
};

function IncidentTypeSelector({
  taskPlan,
  incidentTypes,
  canUpdate,
  onChange,
}: IncidentTypeSelectorProps) {
  const [selectedValues, setSelectedValues] = useState<string[]>(
    taskPlan.incidentTypeIds ?? [],
  );

  useEffect(() => {
    setSelectedValues(taskPlan.incidentTypeIds ?? []);
  }, [taskPlan.incidentTypeIds]);

  return (
    <MultiSelect
      selectedValues={selectedValues}
      setSelectedValues={setSelectedValues}
      options={incidentTypes.map((incidentType) => ({
        label: getIncidentTypeLabel(incidentType),
        value: incidentType.id,
      }))}
      placeholder="Incident types"
      disabled={!canUpdate || incidentTypes.length === 0}
      onValueChange={(values) => {
        setSelectedValues(values);
        const accepted = onChange(taskPlan, values);
        if (!accepted) {
          setSelectedValues(taskPlan.incidentTypeIds ?? []);
        }
      }}
      maxCount={1}
      modalPopover
      showClearButton={false}
      className="border-border/40 h-7 w-56 bg-white px-1 py-0.5 text-xs leading-normal shadow-none"
    />
  );
}

function TaskPlansStepDangerous() {
  const [activeTaskPlanId, setActiveTaskPlanId] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<
    "title" | "description" | "incidentType" | null
  >(null);
  const [inlineValue, setInlineValue] = useState("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteTaskPlan, setPendingDeleteTaskPlan] =
    useState<MatterTaskPlan | null>(null);
  const { hasActionPermission } = useUser();

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());

  const incidentTypes = useMemo(
    () => matterSettingsData?.data.incidentTypes ?? [],
    [matterSettingsData],
  );
  const matterStatuses = useMemo(
    () => matterSettingsData?.data.statuses ?? [],
    [matterSettingsData],
  );
  const matterRoles = useMemo(
    () => matterSettingsData?.data.roles ?? [],
    [matterSettingsData],
  );
  const taskPlans = useMemo(
    () =>
      sortBy(matterSettingsData?.data.taskPlans ?? [], (taskPlan) =>
        new Date(taskPlan.dateCreated).getTime(),
      ),
    [matterSettingsData],
  );

  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTaskPlan,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTaskPlan,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTaskPlan,
    ACTION_PERMISSION_VALUES.delete,
  );

  const editMatterTaskPlan = useUpdateMatterTaskPlan({ showToast: false });
  const deleteMatterTaskPlan = useDeleteMatterTaskPlan({ showToast: false });

  const closeEditor = () => {
    setActiveTaskPlanId(null);
    setActiveField(null);
    setInlineValue("");
    setInlineError(null);
  };

  const startInlineEdit = (
    taskPlan: MatterTaskPlan,
    field: "title" | "description",
  ) => {
    if (!canUpdate) return;
    setActiveTaskPlanId(taskPlan.id);
    setActiveField(field);
    setInlineValue(
      field === "title" ? taskPlan.title : (taskPlan.description ?? ""),
    );
    setInlineError(null);
  };

  const submitInlineEdit = (taskPlan: MatterTaskPlan) => {
    if (!activeField || activeField === "incidentType") return;
    if (!canUpdate) {
      closeEditor();
      return;
    }

    const validation = inlineFieldSchemas[activeField].safeParse(inlineValue);
    if (!validation.success) {
      setInlineError(validation.error.errors[0]?.message ?? "Invalid value");
      return;
    }

    const nextValue = validation.data;
    const currentValue =
      activeField === "title" ? taskPlan.title : (taskPlan.description ?? "");

    if (nextValue === currentValue) {
      closeEditor();
      return;
    }

    if (
      activeField === "title" &&
      taskPlans.some(
        (plan) =>
          toLower(plan.title) === toLower(nextValue as string) &&
          plan.id !== taskPlan.id,
      )
    ) {
      setInlineError("Task plan title must be unique");
      return;
    }

    const payload: Partial<MatterTaskPlan> &
      Pick<MatterTaskPlan, "id" | "dateModified"> = {
      id: taskPlan.id,
      dateModified: taskPlan.dateModified,
      title: activeField === "title" ? (nextValue as string) : taskPlan.title,
      description:
        activeField === "description"
          ? (nextValue as string)
          : taskPlan.description,
      incidentTypeIds: taskPlan.incidentTypeIds ?? [],
    };
    editMatterTaskPlan.mutate(payload);
    closeEditor();
  };

  const handleIncidentTypeChange = (
    taskPlan: MatterTaskPlan,
    nextValues: string[],
  ) => {
    if (!canUpdate) return false;
    setActiveTaskPlanId(taskPlan.id);
    setActiveField("incidentType");
    const validation = inlineFieldSchemas.incidentType.safeParse(nextValues);
    if (!validation.success) {
      setInlineError(validation.error.errors[0]?.message ?? "Invalid value");
      return false;
    }

    const nextValue = validation.data;
    const currentValue = taskPlan.incidentTypeIds ?? [];
    if (nextValue.length === 0) {
      setInlineError("Incident type is required");
      return false;
    }

    if (isEqual(sortBy(nextValue), sortBy(currentValue))) {
      setInlineError(null);
      return true;
    }

    const payload: Partial<MatterTaskPlan> &
      Pick<MatterTaskPlan, "id" | "dateModified"> = {
      id: taskPlan.id,
      dateModified: taskPlan.dateModified,
      title: taskPlan.title,
      description: taskPlan.description,
      incidentTypeIds: nextValue,
    };
    editMatterTaskPlan.mutate(payload);
    setInlineError(null);
    return true;
  };

  const handleInlineKeyDown = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    taskPlan: MatterTaskPlan,
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitInlineEdit(taskPlan);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeEditor();
    }
  };

  return (
    <div className="space-y-5">
      <ActionConfirmationModal
        title={`Are you sure you want to delete this task plan?`}
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        afterActionFn={async () => {
          if (!pendingDeleteTaskPlan) return;
          await deleteMatterTaskPlan.mutateAsync({
            id: pendingDeleteTaskPlan.id,
          });
          setDeleteModalOpen(false);
          setPendingDeleteTaskPlan(null);
        }}
        onCancel={() => {
          setPendingDeleteTaskPlan(null);
        }}
      />

      {isMatterSettingsLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
        </div>
      ) : isMatterSettingsError ? (
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      ) : taskPlans.length === 0 ? (
        <div className="space-y-2">
          <div className="border-border bg-muted/40 rounded-2xl border border-dashed p-6 text-center">
            <Typography weight="semibold">No task plans yet</Typography>
            <Typography className="text-muted-foreground text-sm">
              Add your first task plan to speed up matter setup.
            </Typography>
          </div>
          {canCreate ? (
            <AddTaskPlanEditor
              taskPlans={taskPlans}
              incidentTypes={incidentTypes}
              isReadOnly={!canUpdate}
              onClose={closeEditor}
            />
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col">
          {taskPlans.map((taskPlan, index) => {
            const isEditingTitle =
              activeTaskPlanId === taskPlan.id && activeField === "title";
            const isEditingDescription =
              activeTaskPlanId === taskPlan.id && activeField === "description";

            return (
              <Fragment key={taskPlan.id}>
                {index > 0 ? <Separator className="my-2" /> : null}
                <div className="hover:bg-muted/30 focus-visible:ring-primary flex flex-col gap-2 rounded-xl px-2 py-1.5 transition focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0 flex-1 lg:flex lg:items-center">
                      <Typography
                        tag="h4"
                        weight="semibold"
                        className="flex items-center gap-2 text-sm leading-tight"
                      >
                        <span className="text-foreground flex-none">
                          <InlineEditableField
                            isEditing={isEditingTitle}
                            value={inlineValue}
                            displayValue={taskPlan.title}
                            canEdit={canUpdate}
                            ariaLabel="Task plan title"
                            inputWidthClass="w-auto max-w-full"
                            autoSize
                            matchDisplaySize
                            onStartEdit={() => {
                              startInlineEdit(taskPlan, "title");
                            }}
                            onChange={(value: string) => {
                              setInlineValue(value);
                              if (inlineError) setInlineError(null);
                            }}
                            onSubmit={() => {
                              submitInlineEdit(taskPlan);
                            }}
                            onKeyDown={(
                              event: KeyboardEvent<
                                HTMLInputElement | HTMLTextAreaElement
                              >,
                            ) => {
                              handleInlineKeyDown(event, taskPlan);
                            }}
                          />
                        </span>
                        <span className="text-muted-foreground min-w-0 flex-1 text-xs leading-snug">
                          <InlineEditableField
                            isEditing={isEditingDescription}
                            value={inlineValue}
                            displayValue={
                              taskPlan.description
                                ? taskPlan.description
                                : "No description"
                            }
                            canEdit={canUpdate}
                            ariaLabel="Task plan description"
                            inputWidthClass="w-auto max-w-full"
                            autoSize
                            textClassName="text-xs"
                            matchDisplaySize
                            onStartEdit={() => {
                              startInlineEdit(taskPlan, "description");
                            }}
                            onChange={(value: string) => {
                              setInlineValue(value);
                              if (inlineError) setInlineError(null);
                            }}
                            onSubmit={() => {
                              submitInlineEdit(taskPlan);
                            }}
                            onKeyDown={(
                              event: KeyboardEvent<
                                HTMLInputElement | HTMLTextAreaElement
                              >,
                            ) => {
                              handleInlineKeyDown(event, taskPlan);
                            }}
                          />
                        </span>
                      </Typography>
                      {activeTaskPlanId === taskPlan.id && inlineError ? (
                        <Typography className="text-destructive text-xs">
                          {inlineError}
                        </Typography>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 lg:flex-nowrap lg:justify-end">
                      <IncidentTypeSelector
                        taskPlan={taskPlan}
                        incidentTypes={incidentTypes}
                        canUpdate={canUpdate}
                        onChange={handleIncidentTypeChange}
                      />
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="xs"
                          disabled={!canDelete}
                          onClick={() => {
                            setPendingDeleteTaskPlan(taskPlan);
                            setDeleteModalOpen(true);
                          }}
                          aria-label={`Delete ${taskPlan.title}`}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <TaskPlanTasksSection
                    taskPlan={taskPlan}
                    statuses={matterStatuses}
                    roles={matterRoles}
                    canCreate={canCreate}
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                  />
                </div>
              </Fragment>
            );
          })}
          {canCreate ? (
            <>
              <Separator className="my-2" />
              <AddTaskPlanEditor
                taskPlans={taskPlans}
                incidentTypes={incidentTypes}
                isReadOnly={!canUpdate}
                onClose={closeEditor}
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function TaskPlansStep() {
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      }
    >
      <TaskPlansStepDangerous />
    </ErrorBoundary>
  );
}
